/**
 * Pets v2/v3 (docs/PETS.md "Pets v2", "Pets v3"), parsed from the generated bundle's `pets` (balance/src/pets.json): the
 * roster (each pet's roam level, its wild chase's health scale and its four abilities: data/petAbilityData.ts), the wild
 * encounter's numbers and the Pet Treats. The rules live in meta/pets.ts (ownership, levels, treats), meta/petAbilities.ts (an ability's numbers at a
 * level) and sim/wildPet.ts (the encounter); the sim reads only the run's own copy of a pet's numbers (sim/petRule.ts
 * PetRule), never this data's abilities.
 */
import { Fix64 } from "../maths/fix64";
import { PET_ABILITY_SLOTS, PetAbilitiesData, PetAbilitySlot, parsePetAbilities } from "./petAbilityData";

/** The four ability slots (data/petAbilityData.ts); the v2 names kept as aliases. */
export const PET_ABILITIES = PET_ABILITY_SLOTS;
export type PetAbilityId = PetAbilitySlot;

export interface PetData {
  readonly id: string;
  readonly name: string;
  /** What it does, in a line (the battle card and the pet sheet). */
  readonly blurb: string;
  /** The enemies.json id of its wild encounter on the road (an enemy whose `wildPet` is this pet). */
  readonly wildEnemy: string;
  /** Pets v3: the campaign level it roams from (its debut: played while the pet is not owned, it is that level's wild pet). */
  readonly roamFromLevel: number;
  /** Pets v3: × its wild chase's health once the player owns it (the boss basis × the chapter's hpMult); One for the pup. */
  readonly wildHpScale: Fix64;
  /** Pets v3: × its first catch's health (the level's base power × firstCatchHp); One for the pup. */
  readonly firstCatchScale: Fix64;
  readonly abilities: PetAbilitiesData;
}

/** The wild encounter (sim/wildPet.ts): when, how long, how strong, what a catch pays in points, the skin chance. */
export interface WildPetData {
  readonly fromLevel: number;
  readonly stageFromPercent: number;
  readonly stageToPercent: number;
  readonly appearDelayTicks: number;
  readonly visibleTicks: number;
  /** × the level's boss HP at the encounter's stage, by chapter (1-based; the last for every chapter past the list). */
  readonly hpMult: readonly Fix64[];
  /**
   * A first catch's health (the player has never caught the pet, so no pup of theirs helps): this many times the
   * level's base enemy power at its stage (levelBase × stagePower), by level where listed, else by chapter.
   */
  readonly firstCatchHp: { readonly levels: ReadonlyMap<number, number>; readonly chapters: readonly number[] } | null;
  /** A troop holding it takes this share of its max HP per strike. */
  readonly biteFraction: Fix64;
  readonly catchPoints: number;
  /** Pets v3, which pet is wild (meta/petWild.ts): an uncaught pet's and a caught one's weight in the draw. */
  readonly uncaughtWeight: number;
  readonly caughtWeight: number;
  /** The base skin chance in basis points (100 = 1 %). */
  readonly skinChanceBp: number;
}

/** A Pet Treat: raises the wild pet's skin chance by `bonusPercent` for one battle; sold for `gems`. */
export interface PetTreatData { readonly id: string; readonly name: string; readonly blurb: string; readonly bonusPercent: number; readonly gems: number }

export interface PetsData {
  readonly list: readonly PetData[];
  /** The level a pet plays at in even-footing runs (Daily Road, challenge codes). */
  readonly evenFootingLevel: number;
  /** The save migration only: a v1 save that cleared this level had the pup (keeps it at level 1). */
  readonly v1UnlockAfterLevel: number;
  readonly wild: WildPetData;
  readonly treats: readonly PetTreatData[];
  readonly treatMaxOwned: number;
}

/** Campaign levels per chapter (the wild pet's HP multiplier is per chapter). */
const LEVELS_PER_CHAPTER = 10;
/** Basis points in one (the skin chance's scale). */
export const BASIS_POINTS = 10_000;

const NO_WILD: WildPetData = { fromLevel: Number.MAX_SAFE_INTEGER, stageFromPercent: 30, stageToPercent: 70, appearDelayTicks: 0, visibleTicks: 1, hpMult: [Fix64.One], firstCatchHp: null, biteFraction: Fix64.One, catchPoints: 0, uncaughtWeight: 1, caughtWeight: 1, skinChanceBp: 0 };
export const emptyPets = (): PetsData => ({ list: [], evenFootingLevel: 1, v1UnlockAfterLevel: Number.MAX_SAFE_INTEGER, wild: NO_WILD, treats: [], treatMaxOwned: 0 });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json, where: string, min = 0): number {
  if (typeof v === "number" && Number.isInteger(v) && v >= min) return v;
  throw new TypeError(`${where}: expected an integer >= ${min}, got ${JSON.stringify(v)}`);
}
function str(v: Json, where: string): string {
  if (typeof v === "string" && v.length > 0) return v;
  throw new TypeError(`${where}: expected a non-empty string`);
}
function pos(v: Json, where: string): Fix64 {
  const f = Fix64.tryParse(String(v));
  if (f === null || !f.isPositive()) throw new RangeError(`${where}: expected a number above 0`);
  return f;
}

function parseWild(x: Json): WildPetData {
  const w = "pets.wild";
  const from = int(x?.stageFromPercent, `${w}.stageFromPercent`, 1), to = int(x?.stageToPercent, `${w}.stageToPercent`, 1);
  if (from > to || to >= 100) throw new RangeError(`${w}: 0 < stageFromPercent <= stageToPercent < 100`);
  const skinChanceBp = int(x.skinChanceBp, `${w}.skinChanceBp`);
  if (skinChanceBp > BASIS_POINTS) throw new RangeError(`${w}.skinChanceBp: at most ${BASIS_POINTS}`);
  const biteFraction = pos(x.biteFraction, `${w}.biteFraction`);
  if (biteFraction.gt(Fix64.One)) throw new RangeError(`${w}.biteFraction: at most 1`);
  return {
    fromLevel: int(x.fromLevel, `${w}.fromLevel`, 1), stageFromPercent: from, stageToPercent: to,
    appearDelayTicks: int(x.appearDelayTicks, `${w}.appearDelayTicks`), visibleTicks: int(x.visibleTicks, `${w}.visibleTicks`, 1),
    hpMult: parseHpMult(x.hpMult, `${w}.hpMult`), firstCatchHp: x.firstCatchHp === undefined ? null : parseFirstCatchHp(x.firstCatchHp, `${w}.firstCatchHp`), biteFraction, catchPoints: int(x.catchPoints, `${w}.catchPoints`), skinChanceBp,
    uncaughtWeight: int(x.uncaughtWeight ?? 1, `${w}.uncaughtWeight`, 1), caughtWeight: int(x.caughtWeight ?? 1, `${w}.caughtWeight`, 1),
  };
}

/** firstCatchHp: { levels: { "<level>": units }, chapters: [units per chapter] }, every number a whole number >= 1. */
function parseFirstCatchHp(v: Json, w: string): { levels: Map<number, number>; chapters: number[] } {
  const chapters = (Array.isArray(v?.chapters) ? v.chapters : []).map((x: Json, i: number) => int(x, `${w}.chapters[${i}]`, 1));
  if (chapters.length === 0) throw new RangeError(`${w}.chapters: one per chapter`);
  const levels = new Map<number, number>();
  for (const [k, x] of Object.entries((v?.levels ?? {}) as Record<string, Json>)) {
    const l = Number(k);
    if (!Number.isInteger(l) || l < 1) throw new RangeError(`${w}.levels: '${k}' is not a level`);
    levels.set(l, int(x, `${w}.levels.${k}`, 1));
  }
  return { levels, chapters };
}

/** A first catch's health units for campaign level `levelNumber` (its own entry, else its chapter's; the last past the list); null without the block (a first catch is then the ordinary chase). */
export function firstCatchHpAt(data: PetsData, levelNumber: number): number | null {
  const f = data.wild.firstCatchHp;
  if (f === null) return null;
  const own = f.levels.get(levelNumber);
  if (own !== undefined) return own;
  const chapter = Math.floor((Math.max(1, levelNumber) - 1) / LEVELS_PER_CHAPTER);
  return f.chapters[Math.min(chapter, f.chapters.length - 1)];
}

/** One multiplier, or one per chapter (a non-empty list). */
function parseHpMult(v: Json, w: string): Fix64[] {
  const list = Array.isArray(v) ? v : [v];
  if (list.length === 0) throw new RangeError(`${w}: one multiplier or one per chapter`);
  return list.map((x: Json, i: number) => pos(x, `${w}[${i}]`));
}

/** The wild pet's HP multiplier for campaign level `levelNumber` (its chapter's; the last past the list). */
export function wildHpMultAt(data: PetsData, levelNumber: number): Fix64 {
  const m = data.wild.hpMult, chapter = Math.floor((Math.max(1, levelNumber) - 1) / LEVELS_PER_CHAPTER);
  return m[Math.min(chapter, m.length - 1)];
}
function parseTreats(list: Json, skinChanceBp: number): PetTreatData[] {
  const ids = new Set<string>();
  return (Array.isArray(list) ? list : []).map((t: Json, i: number): PetTreatData => {
    const w = `pets.treats[${i}]`;
    const id = str(t?.id, `${w}.id`);
    if (ids.has(id)) throw new Error(`${w}: duplicate id '${id}'`);
    ids.add(id);
    const bonusPercent = int(t.bonusPercent, `${w}.bonusPercent`, 1);
    if (Math.floor((skinChanceBp * (100 + bonusPercent)) / 100) > BASIS_POINTS) throw new RangeError(`${w}: the raised skin chance passes 100 %`);
    return { id, name: str(t.name, `${w}.name`), blurb: typeof t.blurb === "string" ? t.blurb : "", bonusPercent, gems: int(t.gems, `${w}.gems`, 1) };
  });
}

/** The bundle's `pets`; absent (an older bundle): no pets. `maxLevel`: the towers' max (tables.maxLevel). */
export function parsePets(root: Json, maxLevel = Number.MAX_SAFE_INTEGER): PetsData {
  const p = root?.pets;
  if (p === undefined || p === null) return emptyPets();
  const ids = new Set<string>();
  const list = (Array.isArray(p.pets) ? p.pets : []).map((x: Json, i: number): PetData => {
    const w = `pets.pets[${i}]`;
    const id = str(x?.id, `${w}.id`);
    if (ids.has(id)) throw new Error(`${w}: duplicate id '${id}'`);
    ids.add(id);
    return {
      id, name: str(x.name, `${w}.name`), blurb: str(x.blurb, `${w}.blurb`), wildEnemy: str(x.wildEnemy, `${w}.wildEnemy`),
      roamFromLevel: int(x.roamFromLevel ?? 1, `${w}.roamFromLevel`, 1), wildHpScale: x.wildHpScale === undefined ? Fix64.One : pos(x.wildHpScale, `${w}.wildHpScale`),
      firstCatchScale: x.firstCatchScale === undefined ? Fix64.One : pos(x.firstCatchScale, `${w}.firstCatchScale`),
      abilities: parsePetAbilities(x.abilities, `${w}.abilities`, maxLevel),
    };
  });
  const evenFootingLevel = int(p.evenFootingLevel ?? 1, "pets.evenFootingLevel", 1);
  const wild = parseWild(p.wild);
  return {
    list, evenFootingLevel, v1UnlockAfterLevel: int(p.v1UnlockAfterLevel ?? 1, "pets.v1UnlockAfterLevel", 1), wild,
    treats: parseTreats(p.treats, wild.skinChanceBp), treatMaxOwned: int(p.treatMaxOwned ?? 0, "pets.treatMaxOwned"),
  };
}

/** The wild pet's skin chance in basis points with `treat` armed (null: none): base × (1 + bonus %), floored. */
export function skinChanceBp(data: PetsData, treat: PetTreatData | null): number {
  const b = data.wild.skinChanceBp;
  return treat === null ? b : Math.min(BASIS_POINTS, Math.floor((b * (100 + treat.bonusPercent)) / 100));
}
